<template>
  <!-- NAV -->
  <div v-if="mode === 'detail'" class="d-flex align-items-center gap-2 mb-3" style="font-size: 0.875rem">
    <a class="text-primary fw-semibold" style="cursor: pointer"
      @click="$router.push($route.query.from || '/monitoring-document')">
      Perjanjian Kerja Sama
    </a>
    <i class="bi bi-chevron-right text-muted" style="font-size: 12px"></i>
    <span class="text-muted">Details</span>
  </div>
  
  <div v-else class="mb-3">
    <a class="d-flex align-items-center gap-2 text-primary fw-semibold" style="cursor: pointer; width: fit-content"
      @click="goBack">
      <i class="bi bi-arrow-left"></i> Back
    </a>
  </div>
  
  <div class="card-modern p-4">
    <!-- HEADER -->
    <div class="d-flex justify-content-between align-items-start mb-4">
      <div>
        <h3 class="fw-bold text-dark mb-1">{{ pageTitle }}</h3>
        <p class="card-desc-modern mb-0">{{ pageDescription }}</p>
      </div>
      <div v-if="mode === 'detail'" class="d-flex gap-2">
        <button class="btn btn-secondary" @click="goBack">Back</button>
        <button class="btn btn-primary" :disabled="isDetailEditLocked"
          :title="isDetailEditLocked ? detailEditLockTitle : 'Edit'" @click="goToEdit(detail.document_id)">
          <i class="bi bi-pencil-square me-1"></i> Edit
        </button>
      </div>
    </div>
  
    <!-- ════════════════ DETAIL ════════════════ -->
    <template v-if="mode === 'detail'">
      <!-- Alerts -->
      <div v-if="detail.expired_status" class="mb-3">
        <div v-if="['EXPIRED', 'EXPIRED TODAY'].includes(detail.expired_status)" class="alert alert-danger border-danger">
          <strong>
            ⚠️
            {{
            detail.expired_status === "EXPIRED"
            ? "DOKUMEN SUDAH EXPIRED"
            : "DOKUMEN EXPIRED HARI INI"
            }}
          </strong>
          <br />
          <template v-if="detail.expired_status === 'EXPIRED'">
            Telah lewat
            <strong>{{ getDaysText(detail.days_until_expired) }} hari</strong>
          </template>
          <template v-else>
            Dokumen telah expired hari ini ({{ formatDate(detail.end_date) }})
          </template>
        </div>
        <div v-else-if="detail.expired_status === 'SOON EXPIRED'" class="alert alert-warning">
          <strong>⚠️ Peringatan Expired</strong><br />
          Akan expired dalam
          <strong>{{ getDaysText(detail.days_until_expired) }} hari</strong>
        </div>
        <div v-else-if="isDocumentInactiveOrSuperseded" class="alert alert-secondary">
          <strong>Dokumen tidak dapat diedit</strong><br />
          Status:
          <strong>{{
            detail.expired_status || detail.status_name || "INACTIVE"
            }}</strong>
          <template v-if="detail.inactive_reason_name || detail.inactive_reason">
            — {{ detail.inactive_reason_name || detail.inactive_reason }}
          </template>
        </div>
      </div>
  
      <div v-else-if="isDetailComplete && !canEditWhenComplete" class="alert alert-warning mb-3">
        ⚠️ Progres dokumen ini sudah <strong>Complete</strong> (Renewal AUTO)
        dan tidak dapat diedit. Gunakan fitur <strong>Add Addendum</strong>
        untuk perpanjangan.
      </div>
  
      <!-- Adendum dari ref_id -->
      <div v-if="detail.ref_id" class="alert alert-info mb-3">
        Dokumen ini adalah
        <strong>{{ detail.description_name || "Addendum" }}</strong> dari
        dokumen:
        <strong class="text-primary ms-1 text-decoration-underline" style="cursor: pointer" @click="
              $router.push({
                path: `/detail-document/${detail.ref_id}`,
                query: { from: $route.query.from },
              })
            ">
          {{ parentDetail?.document_number || "-" }}
        </strong>
      </div>
  
      <!-- Lanjutan dari prev_document_id (SPK→SPK / SPK→PKS) -->
      <div v-if="detail.prev_document_id" class="alert alert-info mb-3">
        Dokumen ini adalah <strong>lanjutan</strong> dari:
        <strong class="text-primary ms-1 text-decoration-underline" style="cursor: pointer" @click="
              $router.push({
                path: `/detail-document/${detail.prev_document_id}`,
                query: { from: $route.query.from },
              })
            ">
          {{
          detail.prev_document_number ||
          detail.ref_document_number ||
          "#" + detail.prev_document_id
          }}
        </strong>
      </div>
  
      <!-- Informasi Dokumen -->
      <div class="card shadow-sm border-0 mb-3">
        <div class="card-body">
          <h6 class="section-title">
            <span class="icon-box"><i class="bi bi-file-earmark-text"></i></span>
            Informasi Dokumen
          </h6>
  
          <div class="row g-3">
            <div class="col-md-3">
              <label class="form-label fw-semibold">Nama Perusahaan</label>
              <div class="form-control bg-light">
                {{ detail.mst_corporate_name || "-" }}
              </div>
            </div>
            <div class="col-md-3">
              <label class="form-label fw-semibold">Company</label>
              <div class="form-control bg-light">
                {{ detail.company_name || "-" }}
              </div>
            </div>
            <div class="col-md-3">
              <label class="form-label fw-semibold">Partner Type</label>
              <div class="form-control bg-light">
                {{ detail.partner_type_name || "-" }}
                <template v-if="detail.partner_type_other">
                  <span class="text-muted">(</span>{{ detail.partner_type_other
                  }}<span class="text-muted">)</span>
                </template>
              </div>
            </div>
            <div class="col-md-3">
              <label class="form-label fw-semibold">Department</label>
              <div class="form-control bg-light">
                {{ detail.mst_division_name || "-" }}
              </div>
            </div>
  
            <div class="col-md-3">
              <label class="form-label fw-semibold">Nomor Dokumen</label>
              <div class="form-control bg-light text-primary fw-semibold">
                {{ detail.document_number || "-" }}
              </div>
            </div>
            <div class="col-md-3">
              <label class="form-label fw-semibold">Keterangan</label>
              <div class="form-control bg-light">
                {{ detail.description_name || "-" }}
              </div>
            </div>
            <div class="col-md-3">
              <label class="form-label fw-semibold">Progress Dokumen</label>
              <div class="form-control bg-light">
                {{ detail.progress_status_name || "-" }}
              </div>
            </div>
            <div class="col-md-3">
              <label class="form-label fw-semibold">Renewal Type</label>
              <div class="form-control bg-light">
                {{ detail.renewal_type_name || "-" }}
              </div>
            </div>
  
            <div class="col-md-3">
              <label class="form-label fw-semibold">Tanggal Mulai</label>
              <div class="form-control bg-light">
                {{ formatDate(detail.start_date) }}
              </div>
            </div>
            <div class="col-md-3">
              <label class="form-label fw-semibold">Tanggal Selesai</label>
              <div class="form-control bg-light">
                {{ formatDate(detail.end_date) }}
              </div>
            </div>
            <div class="col-md-3">
              <label class="form-label fw-semibold">Status</label>
              <div class="form-control bg-light">
                <span :class="getStatusClass(detail.status_name)">{{
                  detail.status_name
                  }}</span>
              </div>
            </div>
            <div class="col-md-3" v-if="detail.inactive_reason_name || detail.inactive_reason">
              <label class="form-label fw-semibold">Alasan Inactive</label>
              <div class="form-control bg-light">
                {{ detail.inactive_reason_name || detail.inactive_reason }}
              </div>
            </div>
          </div>
        </div>
      </div>
  
      <!-- File Dokumen -->
      <div class="card shadow-sm border-0 mb-3">
        <div class="card-body">
          <h6 class="section-title">
            <i class="bi bi-file-earmark-pdf me-2"></i> Dokumen Perjanjian Kerja
            Sama
          </h6>
  
          <div v-if="detail.file_path"
            class="border rounded-3 py-2 px-3 mb-2 d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center gap-3">
              <i class="bi fs-2" :class="fileIconClass(detail.file_path)"></i>
              <div class="fw-semibold">{{ getFileName(detail.file_path) }}</div>
            </div>
            <div class="d-flex gap-2">
              <button class="btn btn-outline-primary" title="Lihat" @click="downloadFile(detail.file_path)">
                <i class="bi bi-eye"></i>
              </button>
              <button class="btn btn-outline-secondary" title="Download" @click="downloadFile(detail.file_path)">
                <i class="bi bi-download"></i>
              </button>
            </div>
          </div>
          <div v-else class="border rounded p-4 text-center text-muted">
            Belum ada file yang diupload
          </div>
        </div>
      </div>
  
      <!-- Dokumen Legalitas -->
      <div class="card shadow-sm border-0 mb-3">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h6 class="section-title mb-0">
              <i class="bi bi-file-earmark-pdf me-2"></i>
              Dokumen Legalitas
              <span class="badge bg-light text-secondary ms-1">{{
                checklist.company_type
                }}</span>
            </h6>
            <span :class="[
                  'badge',
                  checklist.is_complete ? 'bg-success' : 'bg-warning text-dark',
                ]">
              <i :class="[
                    'bi',
                    checklist.is_complete
                      ? 'bi-check-circle'
                      : 'bi-exclamation-triangle',
                    'me-1',
                  ]"></i>
              {{ checklist.is_complete ? "Lengkap" : "Belum Lengkap" }}
            </span>
          </div>
  
          <table class="table table-bordered align-middle mb-0">
            <thead class="table-light">
              <tr>
                <th style="width: 40px">No</th>
                <th>Dokumen</th>
                <th style="width: 160px">Status</th>
                <th style="width: 120px">Aksi</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in checklist.items" :key="item.key">
                <td>{{ idx + 1 }}</td>
                <td>
                  {{ item.label }}
                  <span v-if="!item.required" class="text-muted">(opsional)</span>
                </td>
                <td>
                  <span v-if="item.file_path" class="badge bg-success">Sudah Upload</span>
                  <span v-else-if="item.required" class="badge bg-danger">Belum Ada</span>
                  <span v-else class="badge bg-secondary">-</span>
                </td>
                <td>
                  <button v-if="item.file_path" class="btn btn-sm btn-outline-primary" title="Lihat File"
                    @click="downloadFile(item.file_path)">
                    <i class="bi bi-eye"></i> Lihat
                  </button>
                  <span v-else class="text-muted">-</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
  
      <!-- Riwayat -->
      <div class="card shadow-sm border-0">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <h6 class="section-title mb-0">
              <i class="bi bi-clock-history me-2"></i> Riwayat Perubahan Dokumen
            </h6>
            <div class="d-flex gap-2 flex-wrap">
              <button v-if="canContinueToSpk" type="button" class="btn btn-outline-primary" @click="goToContinueSpk">
                Lanjutkan ke SPK
              </button>
              <button v-if="canContinueToPks" type="button" class="btn btn-primary" @click="goToContinuePks">
                Lanjutkan ke PKS
              </button>
              <button class="btn btn-success" :disabled="!isDetailComplete || isDocumentInactiveOrSuperseded" :title="
                    !isDetailComplete
                      ? 'Progress dokumen harus Complete terlebih dahulu'
                      : ''
                  " @click="goToAddAddendum">
                + Add Addendum
              </button>
            </div>
          </div>
  
          <table v-if="riwayatRows.length" class="table table-bordered align-middle">
            <thead class="table-light">
              <tr>
                <th>Keterangan</th>
                <th>Nomor Dokumen</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in riwayatRows" :key="item.document_id">
                <td>
                  {{ item.description_name }}
                  <span v-if="item.is_current" class="text-muted small">(saat ini)</span>
                </td>
                <td>
                  <span v-if="item.is_current" class="fw-semibold">{{
                    item.document_number
                    }}</span>
                  <span v-else class="fw-bold text-primary text-decoration-underline" style="cursor: pointer" @click="
                        $router.push({
                          path: `/detail-document/${item.document_id}`,
                          query: { from: $route.query.from },
                        })
                      ">{{ item.document_number }}</span>
                </td>
              </tr>
            </tbody>
          </table>
  
          <div v-else class="text-center py-4 text-muted">
            <i class="bi bi-file-earmark-text fs-1"></i>
            <div class="mt-2">Belum ada riwayat perubahan dokumen</div>
            <small>Tambahkan addendum atau lanjutkan SPK/PKS untuk mencatat
              perubahan.</small>
          </div>
        </div>
      </div>
    </template>
  
    <!-- ════════════════ ADD / EDIT ════════════════ -->
    <template v-else>
      <!-- Adendum -->
      <div v-if="form.ref_id" class="alert alert-info mb-3">
        Sedang menambahkan <strong>{{ documentTypeName }}</strong>. Dokumen
        Perjanjian Kerja Sama:
        <strong class="text-primary ms-1 text-decoration-underline" style="cursor: pointer"
          @click="$router.push(`/detail-document/${form.ref_id}`)">
          {{ refDocumentNumber }}
        </strong>
      </div>
  
      <!-- Lanjutan SPK/PKS (prev_document_id) -->
      <div v-if="isContinuation" class="alert alert-info mb-3">
        Sedang membuat <strong>lanjutan</strong> dari dokumen:
        <strong class="text-primary ms-1 text-decoration-underline" style="cursor: pointer"
          @click="$router.push(`/detail-document/${form.prev_document_id}`)">
          {{ refDocumentNumber || "#" + form.prev_document_id }}
        </strong>
      </div>
  
      <!-- Form utama -->
      <div class="card shadow-sm border-0 mt-3">
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-3">
              <label class="form-label fw-semibold">Nama Perusahaan <span class="text-danger">*</span></label>
              <Multiselect v-model="form.mst_corporate_id" :options="corporateList" value-prop="mst_corporate_id"
                track-by="mst_corporate_name" label="mst_corporate_name" placeholder="--Pilih Perusahaan--"
                :searchable="true" :can-clear="!isCorporateLocked && !isManualCompleteExtend"
                :disabled="isCorporateLocked || isManualCompleteExtend" no-options-text="Tidak ada data"
                no-results-text="Perusahaan tidak ditemukan" />
            </div>
  
            <div class="col-md-3">
              <label class="form-label fw-semibold">Company <span class="text-danger">*</span></label>
              <Multiselect v-model="form.company" :options="companyList" value-prop="mst_corporate_id"
                label="mst_corporate_alias" track-by="mst_corporate_alias" placeholder="-- Pilih Company --"
                :searchable="true" :can-clear="mode !== 'edit'" :disabled="mode === 'edit'" />
            </div>
  
            <div class="col-md-3">
              <div class="row g-3">
                <div :class="isOtherPartnerType ? 'col-md-6' : 'col-md-12'">
                  <label class="form-label fw-semibold">Partner Type <span class="text-danger">*</span></label>
                  <Multiselect v-model="form.partner_type_id" :options="partnerTypeList" value-prop="partner_type_id"
                    track-by="partner_type_name" label="partner_type_name" placeholder="--Pilih Tipe Partner--"
                    :searchable="true" :can-clear="mode !== 'edit'" :disabled="mode === 'edit'"
                    no-options-text="Tidak ada data" no-results-text="Tipe partner tidak ditemukan" />
                </div>
                <div class="col-md-3" v-if="isOtherPartnerType">
                  <label class="form-label fw-semibold d-block">&nbsp;</label>
                  <input type="text" class="form-control" v-model="form.partner_type_other"
                    placeholder="Ketik jenis perjanjian kerja sama lainnya" maxlength="150"
                    :disabled="mode === 'detail'" />
                </div>
              </div>
            </div>
  
            <div class="col-md-3">
              <label class="form-label fw-semibold">Department</label>
              <div class="form-control bg-light">
                {{ selectedPartnerTypeDivisionName }}
              </div>
            </div>
  
            <div class="col-md-3">
              <label class="form-label fw-semibold">Nomor Dokumen <span class="text-danger">*</span></label>
              <input v-model="form.document_number" type="text" class="form-control" placeholder="Cth: FLI/001/2026"
                style="text-transform: uppercase" :readonly="mode === 'edit'" :class="{ 'bg-light': mode === 'edit' }"
                @input="
                    form.document_number = form.document_number.toUpperCase()
                  " />
            </div>
  
            <div class="col-md-3">
              <label class="form-label fw-semibold">Keterangan <span class="text-danger">*</span></label>
  
              <Multiselect v-if="form.ref_id" v-model="form.description" :options="filteredDescriptionList"
                value-prop="value" label="description" track-by="description" placeholder="-- Pilih Keterangan --"
                :searchable="true" :can-clear="mode !== 'edit' && !isManualCompleteExtend"
                :disabled="mode === 'edit' || isManualCompleteExtend" />
  
              <div v-else-if="isSpkForced" class="form-control bg-light">
                {{ lockedDescriptionLabel }}
              </div>
  
              <Multiselect v-else v-model="form.description" :options="mainDescriptionList" value-prop="value"
                label="description" track-by="description" placeholder="-- Pilih Keterangan --" :searchable="true"
                :can-clear="false" :disabled="
                    mode === 'edit' || mode === 'detail' || isManualCompleteExtend
                  " />
            </div>
  
            <div class="col-md-3">
              <label class="form-label fw-semibold">Progress Dokumen <span class="text-danger">*</span></label>
              <Multiselect v-model="form.progress_status" :options="progressList" value-prop="value" label="description"
                track-by="description" placeholder="-- Pilih Progress --" :searchable="true"
                :can-clear="!isManualCompleteExtend" :disabled="isManualCompleteExtend" />
            </div>
  
            <div class="col-md-3">
              <label class="form-label fw-semibold">Renewal Type <span class="text-danger">*</span></label>
              <Multiselect v-model="form.renewal_type" :options="renewalList" value-prop="value" label="description"
                track-by="description" placeholder="-- Pilih Renewal --" :searchable="true" :can-clear="true" />
            </div>
  
            <div class="col-md-3">
              <label class="form-label fw-semibold">Tanggal Mulai <span class="text-danger">*</span></label>
              <input v-model="form.start_date" type="date" class="form-control" :readonly="isManualCompleteExtend"
                :class="{ 'bg-light': isManualCompleteExtend }" />
            </div>
            <div class="col-md-3">
              <label class="form-label fw-semibold">Tanggal Selesai <span class="text-danger">*</span></label>
              <input v-model="form.end_date" type="date" class="form-control" :min="form.start_date" />
            </div>
  
            <div class="col-md-3">
              <label class="form-label fw-semibold">Status Dokumen</label>
              <Multiselect v-model="form.status" :options="statusList" value-prop="value" label="description"
                track-by="description" placeholder="-- Pilih Status --" :searchable="true" :can-clear="true" />
            </div>
  
            <!-- Alasan Inactive -->
            <div class="col-md-3" v-if="isStatusInactive">
              <label class="form-label fw-semibold">
                Alasan Inactive <span class="text-danger">*</span>
              </label>
              <Multiselect v-model="inactiveReasonSelect" :options="inactiveReasonOptions" value-prop="value"
                label="description" track-by="description" placeholder="-- Pilih Alasan --" :searchable="true"
                :can-clear="false" />
              <input v-if="isInactiveReasonOther" v-model="form.inactive_reason_other" type="text"
                class="form-control mt-2" placeholder="Ketik alasan inactive..." maxlength="255" />
            </div>
  
            <div v-if="mode === 'edit' && form.existing_file && !form.file_path" class="col-12">
              <label class="form-label">File Saat Ini</label>
              <div class="d-flex align-items-center justify-content-between px-3 py-2 border rounded-3"
                style="max-width: 400px">
                <div class="d-flex align-items-center gap-2 text-truncate">
                  <i class="bi fs-4" :class="fileIconClass(form.existing_file)"></i>
                  <span class="text-truncate" style="font-size: 0.85rem">{{ getFileName(form.existing_file) }}</span>
                </div>
                <button type="button" class="btn btn-sm btn-outline-primary" @click="downloadFile(form.existing_file)">
                  <i class="bi bi-eye"></i> View
                </button>
              </div>
            </div>
  
            <div class="col-12">
              <label class="form-label fw-semibold">
                File Kontrak / SPK / PO <span class="text-danger">*</span>
              </label>
  
              <div v-if="!form.file_path && !isManualCompleteExtend" class="upload-box mb-2"
                :class="{ dragging: isDragging }" @dragover.prevent="isDragging = true"
                @dragleave.prevent="isDragging = false" @drop.prevent="handleDrop" @click="$refs.fileInput.click()">
                <input ref="fileInput" type="file" :accept="mainFileAccept" class="d-none" @change="handleFileUpload" />
                <i class="bi bi-cloud-upload upload-icon"></i>
                {{ mainFileHint }}
              </div>
  
              <div v-else-if="form.file_path"
                class="d-flex align-items-center justify-content-between px-3 py-2 border rounded-3 bg-light"
                style="max-width: 400px">
                <div class="d-flex align-items-center gap-2">
                  <i class="bi fs-4" :class="fileIconClass(form.file_path.name)"></i>
                  <div>
                    <div class="fw-medium">{{ form.file_path.name }}</div>
                    <small class="text-muted">{{
                      formatFileSize(form.file_path.size)
                      }}</small>
                  </div>
                </div>
                <button type="button" class="btn btn-sm btn-outline-danger" @click="removeSelectedFile"
                  v-if="!isManualCompleteExtend">
                  <i class="bi bi-x-lg"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
  
      <hr />
  
      <!-- Dokumen Legalitas-->
      <div class="card shadow-sm border-0 mt-3">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-1">
            <h3 class="section-title">
              Dokumen Legalitas
              <span v-if="checklist.company_type" class="badge bg-light text-secondary ms-1">
                {{ checklist.company_type }}
              </span>
            </h3>
          </div>
  
          <div v-if="!checklist.items.length" class="text-muted text-center py-3">
            List dokumen legalitas akan muncul setelah perusahaan dipilih.
          </div>
  
          <div v-for="item in checklist.items" :key="item.key" class="mb-3">
            <label class="form-label fw-semibold d-flex align-items-center gap-2 mb-1">
              {{ item.label }}
              <span v-if="!item.required" class="text-muted fw-normal">(opsional)</span>
              <span v-if="
                    item.required &&
                    !checklistPendingFiles[item.key] &&
                    !item.file_path
                  " class="badge bg-danger">
                Belum Ada
              </span>
            </label>
  
            <div v-if="checklistPendingFiles[item.key]"
              class="d-flex align-items-center justify-content-between px-3 py-2 border rounded-3 bg-light mb-2"
              style="max-width: 400px">
              <div class="d-flex align-items-center gap-2 text-truncate">
                <i class="bi fs-4" :class="fileIconClass(checklistPendingFiles[item.key].name)"></i>
                <div class="text-truncate">
                  <div class="fw-medium text-truncate">
                    {{ checklistPendingFiles[item.key].name }}
                  </div>
                  <small class="text-muted">{{
                    formatFileSize(checklistPendingFiles[item.key].size)
                    }}</small>
                </div>
              </div>
              <button type="button" class="btn btn-sm btn-outline-danger" title="Batalkan"
                @click="cancelPendingChecklistFile(item.key)">
                <i class="bi bi-x-lg"></i>
              </button>
            </div>
  
            <div v-else-if="item.file_path"
              class="d-flex align-items-center justify-content-between px-3 py-2 border rounded-3 mb-2"
              style="max-width: 400px">
              <div class="d-flex align-items-center gap-2 text-truncate">
                <i class="bi fs-4" :class="fileIconClass(item.file_path)"></i>
                <span class="text-truncate">{{
                  getFileName(item.file_path)
                  }}</span>
              </div>
              <div class="d-flex gap-1">
                <button type="button" class="btn btn-sm btn-outline-primary" title="Lihat"
                  @click="downloadFile(item.file_path)">
                  <i class="bi bi-eye"></i>
                </button>
                <button type="button" class="btn btn-sm btn-outline-danger" title="Hapus"
                  @click="removeChecklistFile(item.key)">
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            </div>
  
            <div v-else class="upload-box upload-box-sm mb-2" :class="{
                  dragging: checklistDragging === item.key,
                  'opacity-50': mode === 'add' && !form.mst_corporate_id,
                }" @dragover.prevent="checklistDragging = item.key" @dragleave.prevent="checklistDragging = null"
              @drop.prevent="handleChecklistDrop(item.key, $event)" @click="triggerChecklistInput(item.key)">
              <input :ref="'checklistInput_' + item.key" type="file"
                accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" class="d-none"
                @change="handleChecklistUpload(item.key, $event)" />
              <i class="bi bi-cloud-upload upload-icon upload-icon-sm"></i>
              <span class="upload-text">Drag &amp; drop file di sini, atau klik untuk upload
                PDF/JPG/JPEG/PNG (maks 10MB)</span>
            </div>
          </div>
  
          <hr v-if="checklist.items.length" class="my-4" />
          <div v-if="checklist.items.length" class="legal-note mt-3">
            <div class="d-flex align-items-center mb-2">
              <i class="bi bi-info-circle-fill text-primary me-2"></i>
              <strong>Catatan</strong>
            </div>
            <ul class="mb-0 ps-3">
              <li>
                NPWP perorangan disesuaikan dengan jenis jasa dan ketentuan
                perpajakan yang berlaku.
              </li>
              <li>
                Apabila pemasok berbentuk PT/CV menggunakan rekening pribadi,
                wajib melampirkan Surat Pernyataan Penggunaan Rekening Pribadi
                beserta bukti kepemilikan rekening.
              </li>
            </ul>
          </div>
        </div>
      </div>
  
      <div class="d-flex justify-content-end gap-2 mt-4">
        <button class="btn btn-secondary" :disabled="isLoading" @click="goBack">
          Cancel
        </button>
        <button class="btn btn-primary" :disabled="isLoading" @click="saveData">
          <span v-if="isLoading" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="bi bi-floppy"></i>
          {{ submitLabel }}
        </button>
      </div>
    </template>
  
    <div v-if="toast.show" :class="['alert', 'alert-' + toast.type, 'position-fixed']" style="
          top: 24px;
          right: 24px;
          z-index: 9999;
          min-width: 300px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        " role="alert">
      {{ toast.message }}
    </div>
  </div>
</template>

<script>
import DocumentFormViewJs from "./DocumentFormView";
export default DocumentFormViewJs;
</script>